import { useCallback, useEffect, useRef, useState } from "react";
import { getSurveyTestLeads } from "api/survey/surveyApi";
import { apiErrorMessage, paginatorFromResponse } from "api/survey/surveyApi.utils";

const emptyLeads = { rows: [], currentPage: 1, lastPage: 1, total: 0, from: 0, to: 0 };

export default function useTestLeadSearch({ show, surveyId }) {
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);
  const activeSearchRef = useRef("");
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState(emptyLeads);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadLeads = useCallback(async (page = 1, term = activeSearchRef.current) => {
    if (!surveyId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const response = await getSurveyTestLeads(surveyId, page, term, 20);
      if (requestId !== requestIdRef.current) return;
      setLeads(paginatorFromResponse(response));
      setAssignedLeads(Array.isArray(response.assignedLeads) ? response.assignedLeads : []);
    } catch (requestError) {
      if (requestId === requestIdRef.current) {
        setError(apiErrorMessage(requestError, "Unable to load leads."));
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [surveyId]);

  useEffect(() => {
    if (!show) {
      requestIdRef.current += 1;
      setLoading(false);
      return undefined;
    }
    setSearch("");
    setAssignedLeads([]);
    activeSearchRef.current = "";
    loadLeads(1, "");
    return () => {
      requestIdRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [loadLeads, show]);

  const changeSearch = (value) => {
    setSearch(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const term = value.trim();
      activeSearchRef.current = term;
      loadLeads(1, term);
    }, 300);
  };

  return { search, changeSearch, leads, assignedLeads, loading, error, setError, loadLeads, activeSearchRef };
}
